import React, { useEffect, useRef, useState } from 'react'
import Header from './Header'
import VideoCarousel from './VideoCarousel'
import LandingUpcomingEvents from './LandingUpcomingEvents'
import { Link } from 'react-router-dom'
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import NamazTimings from './NamazTimings'
import DonationSection from './DonationSection'

gsap.registerPlugin(ScrollTrigger);

function LandingPage() {
  const [verified, setVerified] = useState(false);
  const heroTextRef = useRef(null);
  const imageRef = useRef(null);

  useEffect(() => {
    // Image Animation - Scale and Fade In
    if (imageRef.current) {
      gsap.fromTo(
        imageRef.current,
        {
          scale: 0.8,
          opacity: 0,
          rotateY: -15,
        },
        {
          scale: 1,
          opacity: 1,
          rotateY: 0,
          duration: 1.2,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: imageRef.current,
            start: 'top 80%',
            end: 'top 50%',
            toggleActions: 'play none none reverse',
          },
        }
      );
    }

    // GSAP Text Animation - Gray to Black (for white background)
    if (heroTextRef.current) {
      const words = heroTextRef.current.querySelectorAll('.word');
      
      // Split each word into individual characters
      words.forEach((word) => {
        const text = word.textContent;
        word.innerHTML = text
          .split('')
          .map((char) => `<span class="char">${char === ' ' ? '&nbsp;' : char}</span>`)
          .join('');
      });

      // Get all characters after splitting
      const chars = heroTextRef.current.querySelectorAll('.char');
      const totalChars = chars.length;
      
      // Set initial color to light gray
      chars.forEach((char) => {
        char.style.color = 'rgb(156, 163, 175)'; // gray-400 - initial state
        char.style.opacity = 1;
      });

      // Create ScrollTrigger for character-by-character reveal
      ScrollTrigger.create({
        trigger: heroTextRef.current,
        start: 'top 80%',
        end: 'bottom 10%',
        scrub: 2,
        onUpdate: (self) => {
          chars.forEach((char, index) => {
            // Calculate progress for each character - scroll DOWN reveals black
            const charProgress = Math.max(0, Math.min(1, (self.progress * totalChars * 1.2) - index));
            
            // Apply color change from light gray (156, 163, 175) to black (0, 0, 0)
            const r = Math.round(156 - charProgress * 156);
            const g = Math.round(163 - charProgress * 163);
            const b = Math.round(175 - charProgress * 175);
            
            char.style.color = `rgb(${r}, ${g}, ${b})`;
            char.style.opacity = 1;
          });
        },
      });
    }

    // Cleanup ScrollTrigger instances on unmount
    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  return (
    <div className='bg-[#F5F5F5] pb-[500px] md:pb-[160px] lg:pb-[120px]'>
      <div>
        <Header/>
      </div>

      <VideoCarousel/>

      {/* Our History Section */}
      <div className='mt-12 sm:mt-16 md:mt-20 lg:mt-32 px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16'>
        <div className='max-w-7xl mx-auto'>
          <div className='flex flex-col lg:flex-row justify-between items-center gap-8 lg:gap-12 xl:gap-16'>
            {/* Image Section */}
            <div className='flex justify-center w-full lg:w-1/2'>
              <div className='relative group w-full max-w-sm sm:max-w-md lg:max-w-lg'>
                <img 
                  ref={imageRef}
                  className="object-cover w-full h-[500px] rounded-2xl shadow-2xl transform transition-transform duration-500 group-hover:scale-105" 
                  src="images/histortimg.jpg" 
                  alt="Islamic Centre" 
                />
                {/* Decorative gradient overlay on hover */}
                <div className='absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500' />
              </div>
            </div>  
            
            {/* Text Section */}
            <div className='flex flex-col w-full lg:w-full max-w-2xl'>
              {/* Header */}
              <div className='flex flex-col items-center  mb-4 sm:mb-6 md:mb-8'>
                <p className='font-black text-base sm:text-lg md:text-xl lg:text-2xl text-gray-600 uppercase tracking-wider'>
                  OUR HISTORY
                </p>
                <h2 className='font-black text-2xl sm:text-3xl md:text-4xl lg:text-5xl  text-gray-900 mt-2 md:mt-3 leading-tight text-center lg:text-left'>
                  ABOUT JAMIATE<br />AHLUL HADITH
                </h2>
                <img className="object-cover my-3 sm:my-4 md:my-6 w-[140px] sm:w-[160px] md:w-[180px] lg:w-[200px]" src="images/hrlineimg.png" alt="hrlineimg" />
              </div>
              
              {/* Animated Text Content */}
              <div ref={heroTextRef} className='space-y-3 sm:space-y-4 md:space-y-5'>
                <p className='word text-justify text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl leading-relaxed font-light tracking-wide'>
                  Subai Jamiat Ahle Hadees Andhra Pradesh is a supervising organization overseeing all District and local Jamiats within the state, working under Markazi Jamiat AhleHadees Hind. As Ahle Hadeeths or Salafis, we are Sunni Muslims who adhere to the pristine teachings of the Holy Quran and Sunnah, believing in pure Monotheism (Tauheed Khalis) and rejecting practices not enjoined by Islamic Shariah.Our mission is to elucidate the message of Islam, propagate Islamic principles and values, and teach mutual benevolence, unity, peace, brotherhood, and love of country. We condemn terrorism, as Islam is the religion of peace.
                </p>
              </div>
              
              {/* Call to Action Button */}
              <div className='flex justify-center  mt-6 sm:mt-8 md:mt-10'>
                <Link to="/aboutus">
                  <button className='relative text-white bg-gradient-to-r from-gray-800 to-gray-900 hover:from-gray-900 hover:to-black px-6 sm:px-8 md:px-10 py-2.5 sm:py-3 md:py-4 text-sm sm:text-base md:text-lg font-black rounded-xl transition-all duration-300 hover:scale-105 hover:shadow-2xl overflow-hidden group'>
                    <span className='relative z-10'>LEARN MORE</span>
                    <div className='absolute inset-0 bg-gradient-to-r from-gray-700 to-gray-800 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left' />
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <NamazTimings/>

      {/* Help Our Cause */}

<DonationSection/>

      {/* <div className='mt-12 sm:mt-16 md:mt-20 lg:mt-32 px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16'>
        <div className='max-w-7xl mx-auto'>
          <div className='flex flex-col lg:flex-row justify-between items-center gap-8 lg:gap-12 xl:gap-20'>
            <div className='flex justify-center w-full lg:w-1/2 order-2 lg:order-1'>
              <img className="object-cover w-full h-[400px] sm:h-[500px] md:h-[600px] max-w-sm sm:max-w-md lg:max-w-lg rounded-2xl shadow-2xl" src="images/landingzakat1.jpg" alt="Islamic Centre" />
            </div>  

            <div className='flex flex-col items-center w-full lg:w-1/2 order-1 lg:order-2'>
              <p className='font-black text-base sm:text-lg md:text-xl lg:text-2xl text-gray-600 uppercase tracking-wider'>
                HELP OUR MAIN CAUSE
              </p>
              <p className='font-black text-2xl sm:text-3xl md:text-4xl lg:text-5xl mt-2'>
                MAKE YOUR<br />DONATION
              </p>
              <img className="object-cover my-3 sm:my-4 md:my-6 w-[140px] sm:w-[160px] md:w-[180px] lg:w-[200px]" src="images/hrlineimg.png" alt="hrlineimg" />

              <h1 className='text-xl sm:text-2xl md:text-3xl lg:text-4xl font-black mt-2 text-center uppercase'>
                Education For Children
              </h1>

              <p className='text-justify max-w-xl text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl px-2 mt-3 sm:mt-4 font-light'>
                Support education, community welfare, and humanitarian causes. Your contribution helps provide essential services and improves lives in our community.
              </p>

              <div className='flex flex-wrap justify-center gap-2 sm:gap-3 lg:gap-4 mt-4 sm:mt-6'>
                <button className='border-2 border-black py-2 px-4 sm:px-5 md:px-6 rounded-lg font-black text-xs sm:text-sm md:text-base hover:bg-gray-800 hover:text-white transition-all'>
                  100 ₹
                </button>
                <button className='border-2 border-black py-2 px-4 sm:px-5 md:px-6 rounded-lg font-black text-xs sm:text-sm md:text-base hover:bg-gray-800 hover:text-white transition-all'>
                  250 ₹
                </button>
                <button className='border-2 border-black py-2 px-4 sm:px-5 md:px-6 rounded-lg font-black text-xs sm:text-sm md:text-base hover:bg-gray-800 hover:text-white transition-all'>
                  500 ₹
                </button>
                <button className='border-2 border-black py-2 px-4 sm:px-5 md:px-6 rounded-lg font-black text-xs sm:text-sm md:text-base hover:bg-gray-800 hover:text-white transition-all'>
                  1000 ₹
                </button>
              </div>

              <div className='flex justify-center mt-3 sm:mt-4'>
                <Link to="/donate">
                  <button className='text-white bg-gray-800 hover:bg-gray-900 px-5 sm:px-6 md:px-8 py-2 md:py-3 w-36 sm:w-40 md:w-48 rounded-lg font-black text-xs sm:text-sm md:text-base transition-all hover:scale-105'>
                    DONATE NOW
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div> */}

      {/* Pillars of Islam */}
      <div className="relative w-full mt-12 sm:mt-16 md:mt-20 lg:mt-32">
        <img className="object-cover w-full h-[800px] sm:h-[900px] md:h-[550px]" src="images/pillersimg.png" alt="Background" />

        <div className='absolute inset-0 mt-20 sm:mt-28 md:mt-10 lg:mt-10 xl:mt-20 flex flex-col items-center px-4'>
          <p className='text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-white uppercase'>
            PILLARS OF ISLAM
          </p>
          <img className="object-cover my-3 sm:my-4 w-[140px] sm:w-[160px] md:w-[180px] lg:w-[200px]" src="images/hrlineimg.png" alt="hrlineimg" />

          <div className='flex flex-col gap-4 sm:gap-6 md:flex-row mt-4 sm:mt-6'>
            <div className='flex gap-3 sm:gap-4 justify-center'>
              <Link to="/library">
                <div className='text-center'>
                  <img className="object-cover w-[120px] sm:w-[140px] md:w-[150px] lg:w-full mx-auto" src="images/shahadahimg4.png" alt="Shahadah" />
                  <p className='text-white text-base sm:text-lg md:text-xl font-black mt-2'>SHAHADAH</p>
                  <p className='text-orange-300 text-sm sm:text-base md:text-lg font-light'>(Faith)</p>
                </div>
              </Link>

              <Link to="/library">
                <div className='text-center'>
                  <img className="object-cover w-[110px] sm:w-[130px] md:w-[140px] lg:w-full mx-auto" src="images/shahadahimg5.png" alt="Salah" />
                  <p className='text-white text-base sm:text-lg md:text-xl font-black mt-2 md:mt-4'>SALAH</p>
                  <p className='text-orange-300 text-sm sm:text-base md:text-lg font-light'>(Prayer)</p>
                </div>
              </Link>
            </div>

            <div className='flex gap-3 sm:gap-4 justify-center'>
              <Link to="/library">
                <div className='text-center'>
                  <img className="object-cover w-[110px] sm:w-[130px] md:w-[140px] lg:w-full mx-auto" src="images/shahadahimg1.png" alt="Sawn" />
                  <p className='text-white text-base sm:text-lg md:text-xl font-black mt-2'>SAWM</p>
                  <p className='text-orange-300 text-sm sm:text-base md:text-lg font-light'>(Fasting)</p>
                </div>
              </Link>

              <Link to="/library">
                <div className='text-center'>
                  <img className="object-cover w-[130px] sm:w-[150px] md:w-[160px] lg:w-full mx-auto" src="images/shahadahimg2.png" alt="Hajj" />
                  <p className='text-white text-base sm:text-lg md:text-xl font-black mt-2'>HAJJ</p>
                  <p className='text-orange-300 text-sm sm:text-base md:text-lg font-light'>(Pilgrim)</p>
                </div>
              </Link>
            </div>   

            <Link to="/library">
              <div className='flex flex-col justify-center items-center'>
                <img className="object-cover w-[130px] sm:w-[150px] md:w-[160px] lg:w-full" src="images/shahadahimg3.png" alt="Zakat" />
                <p className='text-white text-base sm:text-lg md:text-xl font-black mt-2 md:mt-4'>ZAKAT</p>
                <p className='text-orange-300 text-sm sm:text-base md:text-lg font-light'>(Almsgiving)</p>
              </div>
            </Link>
          </div>
        </div>
      </div>

      {/* Our Causes */}
      <div className='mt-12 sm:mt-16 md:mt-20 lg:mt-32 px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16'>
        <div className='max-w-7xl mx-auto'>
          <div className='flex flex-col items-center'>
            <p className='font-black text-base sm:text-lg md:text-xl lg:text-2xl text-gray-600 uppercase tracking-wider'>
              NEED HELP FOR
            </p>
            <p className='font-black text-2xl sm:text-3xl md:text-4xl lg:text-5xl mt-2'>OUR CAUSES</p>
            <img className="object-cover my-3 sm:my-4 md:my-6 w-[140px] sm:w-[160px] md:w-[180px] lg:w-[200px]" src="images/hrlineimg.png" alt="hrlineimg" />
            <p className='text-center text-xs sm:text-sm md:text-base lg:text-lg max-w-3xl font-light px-4'>
              Join hands with us to accomplish projects of serving humanity and making this world a better place to live
            </p>
          </div>

          <div className='grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6 sm:gap-8 lg:gap-12 xl:gap-16 mt-6 sm:mt-8 md:mt-12'>
            <div className='bg-[#faf8f8] rounded-xl pb-6 sm:pb-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105'>
              <img className="object-cover w-full h-[220px] sm:h-[240px] md:h-[260px] rounded-t-xl" src="images/masjidcon.jpg" alt="Masjid Construction" />
              <p className='text-center text-base sm:text-lg md:text-xl font-black mt-3 sm:mt-4 px-4 uppercase'>
                Masjid Construction
              </p>
              <div className='flex justify-center mt-3 sm:mt-4'>
                <Link to="/donate">
                  <button className='text-white bg-gray-800 hover:bg-gray-900 px-5 sm:px-6 md:px-8 py-2 md:py-3 w-36 sm:w-40 md:w-48 rounded-lg font-black text-xs sm:text-sm md:text-base transition-all hover:scale-105'>
                    DONATE NOW
                  </button>
                </Link>
              </div>
            </div>
           
            <div className='bg-[#faf8f8] rounded-xl pb-6 sm:pb-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105'>
              <img className="object-cover w-full h-[220px] sm:h-[240px] md:h-[260px] rounded-t-xl" src="images/services3.png" alt="Madrasa Maintenance" />
              <p className='text-center text-base sm:text-lg md:text-xl font-black mt-3 sm:mt-4 px-4 uppercase'>
                Madrasa Maintenance
              </p>
              <div className='flex justify-center mt-3 sm:mt-4'>
                <Link to="/donate">
                  <button className='text-white bg-gray-800 hover:bg-gray-900 px-5 sm:px-6 md:px-8 py-2 md:py-3 w-36 sm:w-40 md:w-48 rounded-lg font-black text-xs sm:text-sm md:text-base transition-all hover:scale-105'>
                    DONATE NOW
                  </button>
                </Link>
              </div>
            </div>

            <div className='bg-[#faf8f8] rounded-xl pb-6 sm:pb-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105'>
              <img className="object-cover w-full h-[220px] sm:h-[240px] md:h-[260px] rounded-t-xl" src="images/cause2.png" alt="Imaam & Mauzin Funds" />
              <p className='text-center text-base sm:text-lg md:text-xl font-black mt-3 sm:mt-4 px-4 uppercase'>
                Imaam & Mauzin Funds
              </p>
              <div className='flex justify-center mt-3 sm:mt-4'>
                <Link to="/donate">
                  <button className='text-white bg-gray-800 hover:bg-gray-900 px-5 sm:px-6 md:px-8 py-2 md:py-3 w-36 sm:w-40 md:w-48 rounded-lg font-black text-xs sm:text-sm md:text-base transition-all hover:scale-105'>
                    DONATE NOW
                  </button>
                </Link>
              </div>
            </div>

            <div className='bg-[#faf8f8] rounded-xl pb-6 sm:pb-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105'>
              <img className="object-cover w-full h-[220px] sm:h-[240px] md:h-[260px] rounded-t-xl" src="images/askulama.jpg" alt="Maktab & Syllabus" />
              <p className='text-center text-base sm:text-lg md:text-xl font-black mt-3 sm:mt-4 px-4 uppercase'>
                Maktab & Syllabus
              </p>
              <div className='flex justify-center mt-3 sm:mt-4'>
                <Link to="/donate">
                  <button className='text-white bg-gray-800 hover:bg-gray-900 px-5 sm:px-6 md:px-8 py-2 md:py-3 w-36 sm:w-40 md:w-48 rounded-lg font-black text-xs sm:text-sm md:text-base transition-all hover:scale-105'>
                    DONATE NOW
                  </button>
                </Link>
              </div>
            </div>

            <div className='bg-[#faf8f8] rounded-xl pb-6 sm:pb-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105'>
              <img className="object-cover w-full h-[220px] sm:h-[240px] md:h-[260px] rounded-t-xl" src="images/charity.png" alt="Social Work" />
              <p className='text-center text-base sm:text-lg md:text-xl font-black mt-3 sm:mt-4 px-4 uppercase'>
                Social Work
              </p>
              <div className='flex justify-center mt-3 sm:mt-4'>
                <Link to="/donate">
                  <button className='text-white bg-gray-800 hover:bg-gray-900 px-5 sm:px-6 md:px-8 py-2 md:py-3 w-36 sm:w-40 md:w-48 rounded-lg font-black text-xs sm:text-sm md:text-base transition-all hover:scale-105'>
                    DONATE NOW
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <LandingUpcomingEvents/>

      {/* CSS for char animation */}
      <style jsx>{`
        .char {
          display: inline-block;
          color: rgb(156, 163, 175); /* gray-400 initial state */
          opacity: 1;
          transition: color 0.3s ease;
        }
      `}</style>
    </div>
  )
}

export default LandingPage


// import React, { useEffect, useRef, useState } from 'react'
// import Header from './Header'
// import VideoCarousel from './VideoCarousel'
// import LandingUpcomingEvents from './LandingUpcomingEvents'
// import { Link } from 'react-router-dom'
// import gsap from 'gsap';
// import { ScrollTrigger } from 'gsap/ScrollTrigger';
// import NamazTimings from './NamazTimings'

// gsap.registerPlugin(ScrollTrigger);

// function LandingPage() {
//   const [verified, setVerified] = useState(false);
//   const heroTextRef = useRef(null);
//   const imageRef = useRef(null);

//   useEffect(() => {
//     // Image Animation - Scale and Fade In
//     if (imageRef.current) {
//       gsap.fromTo(
//         imageRef.current,
//         {
//           scale: 0.8,
//           opacity: 0,
//           rotateY: -15,
//         },
//         {
//           scale: 1,
//           opacity: 1,
//           rotateY: 0,
//           duration: 1.2,
//           ease: 'power3.out',
//           scrollTrigger: {
//             trigger: imageRef.current,
//             start: 'top 80%',
//             end: 'top 50%',
//             toggleActions: 'play none none reverse',
//           },
//         }
//       );
//     }

//     // GSAP Text Animation - Gray to Black (for white background)
//     if (heroTextRef.current) {
//       const words = heroTextRef.current.querySelectorAll('.word');
      
//       // Split each word into individual characters
//       words.forEach((word) => {
//         const text = word.textContent;
//         word.innerHTML = text
//           .split('')
//           .map((char) => `<span class="char">${char === ' ' ? '&nbsp;' : char}</span>`)
//           .join('');
//       });

//       // Get all characters after splitting
//       const chars = heroTextRef.current.querySelectorAll('.char');
//       const totalChars = chars.length;
      
//       // Set initial color to light gray
//       chars.forEach((char) => {
//         char.style.color = 'rgb(156, 163, 175)'; // gray-400 - initial state
//         char.style.opacity = 1;
//       });

//       // Create ScrollTrigger for character-by-character reveal
//       ScrollTrigger.create({
//         trigger: heroTextRef.current,
//         start: 'top 80%',
//         end: 'bottom 10%',
//         scrub: 2,
//         onUpdate: (self) => {
//           chars.forEach((char, index) => {
//             // Calculate progress for each character - scroll DOWN reveals black
//             const charProgress = Math.max(0, Math.min(1, (self.progress * totalChars * 1.2) - index));
            
//             // Apply color change from light gray (156, 163, 175) to black (0, 0, 0)
//             const r = Math.round(156 - charProgress * 156);
//             const g = Math.round(163 - charProgress * 163);
//             const b = Math.round(175 - charProgress * 175);
            
//             char.style.color = `rgb(${r}, ${g}, ${b})`;
//             char.style.opacity = 1;
//           });
//         },
//       });
//     }

//     // Cleanup ScrollTrigger instances on unmount
//     return () => {
//       ScrollTrigger.getAll().forEach(trigger => trigger.kill());
//     };
//   }, []);

//   return (
//     <div className='bg-[#F5F5F5] pb-[500px] md:pb-[160px] lg:pb-[120px]'>
//       <div>
//         <Header/>
//       </div>

//       <VideoCarousel/>

//       {/* Our History Section */}
//      {/* Our History Section */}
// <div className='mt-20 md:mt-32 px-4 sm:px-6 md:px-8 lg:px-12'>
//   <div className='max-w-7xl mx-auto'>
//     <div className='flex flex-col lg:flex-row justify-between items-center gap-8 lg:gap-12 xl:gap-16'>
//       {/* Image Section */}
//       <div className='flex justify-center w-full lg:w-1/2'>
//         <div className='relative group w-full max-w-md lg:max-w-lg'>
//           <img 
//             ref={imageRef}
//             className="object-cover w-full rounded-2xl shadow-2xl transform transition-transform duration-500 group-hover:scale-105" 
//             src="images/cabbaborder.png" 
//             alt="Islamic Centre" 
//           />
//           {/* Decorative gradient overlay on hover */}
//           <div className='absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500' />
//         </div>
//       </div>  
      
//       {/* Text Section */}
//       <div className='flex flex-col w-full lg:w-full max-w-2xl'>
//         {/* Header */}
//         <div className='flex flex-col items-center lg:items-start mb-6 md:mb-8'>
//           <p className='font-semibold text-lg sm:text-xl md:text-2xl text-gray-600 uppercase tracking-wider'>Our History</p>
//           <h2 className='font-bold text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-gray-900 mt-2 md:mt-3 leading-tight text-center lg:text-left'>
//             About Jamiate Ahlul Hadith
//           </h2>
//           <img className="object-cover my-4 md:my-6 w-[160px] sm:w-[180px] md:w-[200px]" src="images/hrlineimg.png" alt="hrlineimg" />
//         </div>
        
//         {/* Animated Text Content */}
//         <div ref={heroTextRef} className='space-y-4 md:space-y-5'>
//           <p className='word text-justify text-base sm:text-lg md:text-2xl leading-relaxed font-normal tracking-wide'>
//             Subai Jamiat Ahle Hadees Andhra Pradesh is a supervising organization overseeing all District and local Jamiats within the state, working under Markazi Jamiat AhleHadees Hind. As Ahle Hadeeths or Salafis, we are Sunni Muslims who adhere to the pristine teachings of the Holy Quran and Sunnah, believing in pure Monotheism (Tauheed Khalis) and rejecting practices not enjoined by Islamic Shariah.Our mission is to elucidate the message of Islam, propagate Islamic principles and values, and teach mutual benevolence, unity, peace, brotherhood, and love of country. We condemn terrorism, as Islam is the religion of peace.
//           </p>
          
//           {/* <p className='word text-justify text-base sm:text-lg md:text-xl leading-relaxed font-normal tracking-wide'>
//             As Ahle Hadeeths or Salafis, we are Sunni Muslims who adhere to the pristine teachings of the Holy Quran and Sunnah, believing in pure Monotheism (Tauheed Khalis) and rejecting practices not enjoined by Islamic Shariah.
//           </p>
          
//           <p className='word text-justify text-base sm:text-lg md:text-xl leading-relaxed font-normal tracking-wide'>
//             Our mission is to elucidate the message of Islam, propagate Islamic principles and values, and teach mutual benevolence, unity, peace, brotherhood, and love of country. We condemn terrorism, as Islam is the religion of peace.
//           </p> */}
//         </div>
        
//         {/* Call to Action Button */}
//         <div className='flex justify-center lg:justify-start mt-8 md:mt-10'>
//           <Link to="/aboutus">
//             <button className='relative text-white bg-gradient-to-r from-gray-800 to-gray-900 hover:from-gray-900 hover:to-black px-8 md:px-10 py-3 md:py-4 text-base md:text-lg rounded-xl font-semibold transition-all duration-300 hover:scale-105 hover:shadow-2xl overflow-hidden group'>
//               <span className='relative z-10'>Learn More</span>
//               <div className='absolute inset-0 bg-gradient-to-r from-gray-700 to-gray-800 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left' />
//             </button>
//           </Link>
//         </div>
//       </div>
//     </div>
//   </div>
// </div>

// <NamazTimings/>

//       {/* Namaz Timings */}
//       {/* <div className='flex flex-col justify-center items-center mt-16 md:mt-24 lg:mt-32 px-4'>
//         <div className='flex flex-col items-center'>
//           <p className='font-semibold text-2xl md:text-3xl lg:text-4xl'>Namaz Timings</p>
//           <img className="object-cover my-4 w-[180px] md:w-[200px]" src="images/hrlineimg.png" alt="hrlineimg" />
//         </div>

//         <div className='block xl:hidden w-full max-w-4xl overflow-x-auto'>
//           <table className='w-full border-collapse text-left'>
//             <thead>
//               <tr className='bg-gray-800 text-white'>
//                 <th className='px-3 sm:px-4 md:px-8 py-2 md:py-4 border text-sm sm:text-base md:text-xl lg:text-2xl border-gray-300'>Prayer</th>
//                 <th className='px-3 sm:px-4 md:px-8 py-2 md:py-4 border text-sm sm:text-base md:text-xl lg:text-2xl border-gray-300'>Adhaan</th>
//                 <th className='px-3 sm:px-4 md:px-8 py-2 md:py-4 border text-sm sm:text-base md:text-xl lg:text-2xl border-gray-300'>Iqamah</th>
//               </tr>
//             </thead>
//             <tbody>
//               <tr className='odd:bg-white even:bg-gray-100'>
//                 <td className='px-3 sm:px-4 md:px-8 py-2 md:py-4 border text-sm sm:text-base md:text-xl lg:text-2xl border-gray-300'>Fajr</td>
//                 <td className='px-3 sm:px-4 md:px-8 py-2 md:py-4 border text-sm sm:text-base md:text-xl lg:text-2xl border-gray-300'>4:45AM</td>
//                 <td className='px-3 sm:px-4 md:px-8 py-2 md:py-4 border text-sm sm:text-base md:text-xl lg:text-2xl border-gray-300'>5:00AM</td>
//               </tr>
//               <tr className='odd:bg-white even:bg-gray-100'>
//                 <td className='px-3 sm:px-4 md:px-8 py-2 md:py-4 border text-sm sm:text-base md:text-xl lg:text-2xl border-gray-300'>Dhuhar</td>
//                 <td className='px-3 sm:px-4 md:px-8 py-2 md:py-4 border text-sm sm:text-base md:text-xl lg:text-2xl border-gray-300'>1:00PM</td>
//                 <td className='px-3 sm:px-4 md:px-8 py-2 md:py-4 border text-sm sm:text-base md:text-xl lg:text-2xl border-gray-300'>1:30PM</td>
//               </tr>
//               <tr className='odd:bg-white even:bg-gray-100'>
//                 <td className='px-3 sm:px-4 md:px-8 py-2 md:py-4 text-sm sm:text-base md:text-xl lg:text-2xl border border-gray-300'>Asr</td>
//                 <td className='px-3 sm:px-4 md:px-8 py-2 md:py-4 text-sm sm:text-base md:text-xl lg:text-2xl border border-gray-300'>4:00PM</td>
//                 <td className='px-3 sm:px-4 md:px-8 py-2 md:py-4 text-sm sm:text-base md:text-xl lg:text-2xl border border-gray-300'>4:15PM</td>
//               </tr>
//               <tr className='odd:bg-white even:bg-gray-100'>
//                 <td className='px-3 sm:px-4 md:px-8 py-2 md:py-4 text-sm sm:text-base md:text-xl lg:text-2xl border border-gray-300'>Magrib</td>
//                 <td className='px-3 sm:px-4 md:px-8 py-2 md:py-4 text-sm sm:text-base md:text-xl lg:text-2xl border border-gray-300'>6:00PM</td>
//                 <td className='px-3 sm:px-4 md:px-8 py-2 md:py-4 text-sm sm:text-base md:text-xl lg:text-2xl border border-gray-300'>6:06PM</td>
//               </tr>
//               <tr className='odd:bg-white even:bg-gray-100'>
//                 <td className='px-3 sm:px-4 md:px-8 py-2 md:py-4 text-sm sm:text-base md:text-xl lg:text-2xl border border-gray-300'>Isha</td>
//                 <td className='px-3 sm:px-4 md:px-8 py-2 md:py-4 text-sm sm:text-base md:text-xl lg:text-2xl border border-gray-300'>8:00PM</td>
//                 <td className='px-3 sm:px-4 md:px-8 py-2 md:py-4 text-sm sm:text-base md:text-xl lg:text-2xl border border-gray-300'>8:30PM</td>
//               </tr>
//               <tr className='odd:bg-white even:bg-gray-100'>
//                 <td className='px-3 sm:px-4 md:px-8 py-2 md:py-4 text-sm sm:text-base md:text-xl lg:text-2xl border border-gray-300'>Jummah</td>
//                 <td className='px-3 sm:px-4 md:px-8 py-2 md:py-4 text-sm sm:text-base md:text-xl lg:text-2xl border border-gray-300'>12:30PM</td>
//                 <td className='px-3 sm:px-4 md:px-8 py-2 md:py-4 text-sm sm:text-base md:text-xl lg:text-2xl border border-gray-300'>1:30PM</td>
//               </tr>
//             </tbody>
//           </table>
//         </div>

//         <div className='hidden xl:flex flex-row gap-6 lg:gap-8 mt-8'>
//           <div className='bg-[#F1F1F1] px-4 py-8 w-40 rounded-xl transition duration-300 ease-in-out transform hover:bg-orange-200 hover:shadow-lg'>
//             <p className='text-center font-bold'>FAJR</p>
//             <p>Adhaan :<span className='text-gray-800'> 4:30AM</span></p>
//             <p>Iqamah :<span className='text-gray-800'> 5:00AM</span></p>
//           </div>

//           <div className='bg-[#F1F1F1] px-4 py-8 w-40 rounded-xl transition duration-300 ease-in-out transform hover:bg-orange-200 hover:shadow-lg'>
//             <p className='text-center font-bold'>ZUHR</p>
//             <p>Adhaan :<span className='text-gray-800'> 1:00PM</span></p>
//             <p>Iqamah :<span className='text-gray-800'> 1:30PM</span></p>
//           </div>

//           <div className='bg-[#F1F1F1] px-4 py-8 w-40 rounded-xl transition duration-300 ease-in-out transform hover:bg-orange-200 hover:shadow-lg'>
//             <p className='text-center font-bold'>ASR</p>
//             <p>Adhaan :<span className='text-gray-800'> 4:00PM</span></p>
//             <p>Iqamah :<span className='text-gray-800'> 4:15PM</span></p>
//           </div>

//           <div className='bg-[#F1F1F1] px-4 py-8 w-40 rounded-xl transition duration-300 ease-in-out transform hover:bg-orange-200 hover:shadow-lg'>
//             <p className='text-center font-bold'>MAGRIB</p>
//             <p>Adhaan :<span className='text-gray-800'> 6:00PM</span></p>
//             <p>Iqamah :<span className='text-gray-800'> 6:06PM</span></p>
//           </div>

//           <div className='bg-[#F1F1F1] px-4 py-8 w-40 rounded-xl transition duration-300 ease-in-out transform hover:bg-orange-200 hover:shadow-lg'>
//             <p className='text-center font-bold'>ISHA</p>
//             <p>Adhaan :<span className='text-gray-800'> 8:00PM</span></p>
//             <p>Iqamah :<span className='text-gray-800'> 8:30PM</span></p>
//           </div>

//           <div className='bg-[#479b83] px-4 py-8 w-40 rounded-xl transition duration-300 ease-in-out transform hover:bg-orange-200 hover:shadow-lg'>
//             <p className='text-center font-bold'>JUMMAH</p>
//             <p>Adhaan :<span className='text-white'> 12:45PM</span></p>
//             <p>Iqamah :<span className='text-white'> 1:30PM</span></p>
//           </div>
//         </div>
//       </div> */}

//       {/* Help Our Cause */}
//       <div className='mt-16 md:mt-24 lg:mt-32 px-4 sm:px-6 md:px-8 lg:px-12'>
//         <div className='max-w-7xl mx-auto'>
//           <div className='flex flex-col md:flex-row justify-between items-center gap-8 lg:gap-12 xl:gap-20'>
//             <div className='flex justify-center w-full md:w-1/2'>
//               <img className="object-cover w-full h-[600px] max-w-md lg:max-w-lg rounded-lg" src="images/landingzakat1.jpg" alt="Islamic Centre" />
//             </div>  

//             <div className='flex flex-col items-center w-full md:w-1/2'>
//               <p className='font-semibold text-lg sm:text-xl md:text-2xl text-gray-800'>Help Our Main Cause</p>
//               <p className='font-semibold text-2xl sm:text-3xl md:text-4xl mt-2'>Make Your Donation</p>
//               <img className="object-cover my-4 w-[180px] md:w-[200px]" src="images/hrlineimg.png" alt="hrlineimg" />

//               <h1 className='text-xl sm:text-2xl md:text-3xl lg:text-4xl font-serif mt-2 text-center'>Education For Children</h1>

//               <p className='text-justify max-w-xl text-sm sm:text-base md:text-lg lg:text-xl px-2 mt-4'>
//                 Support education, community welfare, and humanitarian causes. Your contribution helps provide essential services and improves lives in our community.
//               </p>

//               <div className='flex flex-wrap justify-center gap-2 sm:gap-3 lg:gap-4 mt-6'>
//                 <button className='border border-black py-2 px-4 sm:px-6 rounded-lg font-serif text-sm sm:text-base hover:bg-gray-800 hover:text-white transition-all'>100 ₹</button>
//                 <button className='border border-black py-2 px-4 sm:px-6 rounded-lg font-serif text-sm sm:text-base hover:bg-gray-800 hover:text-white transition-all'>250 ₹</button>
//                 <button className='border border-black py-2 px-4 sm:px-6 rounded-lg font-serif text-sm sm:text-base hover:bg-gray-800 hover:text-white transition-all'>500 ₹</button>
//                 <button className='border border-black py-2 px-4 sm:px-6 rounded-lg font-serif text-sm sm:text-base hover:bg-gray-800 hover:text-white transition-all'>1000 ₹</button>
//               </div>

//               <div className='flex justify-center mt-6'>
//                 <Link to="/donate">
//                   <button className='text-white bg-gray-800 hover:bg-gray-900 px-8 md:px-10 py-3 w-48 rounded-lg font-serif text-base md:text-lg transition-all hover:scale-105'>
//                     Donate Now
//                   </button>
//                 </Link>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>

//       {/* Pillars of Islam */}
//       <div className="relative w-full mt-16 md:mt-24 lg:mt-32">
//         <img className="object-cover object-fit w-full h-[900px] md:h-[450px]" src="images/pillersimg.png" alt="Background" />

//         <div className='absolute inset-0 mt-32 lg:mt-10 xl:mt-20 flex flex-col items-center px-4'>
//           <p className='text-2xl md:text-3xl lg:text-4xl font-serif text-white'>Pillars of Islam</p>
//           <img className="object-cover my-4 w-[180px] md:w-[200px]" src="images/hrlineimg.png" alt="hrlineimg" />

//           <div className='flex flex-col gap-4 md:flex-row'>
//             <div className='flex gap-4'>
//               <Link to="/library">
//                 <div>
//                   <img className="object-cover w-[150px] md:w-full" src="images/shahadahimg4.png" alt="Shahadah" />
//                   <p className='text-white text-center text-lg md:text-xl font-semi-bold'>Shahadah</p>
//                   <p className='text-orange-300 text-center text-lg md:text-xl font-semi-bold'>(Faith)</p>
//                 </div>
//               </Link>

//               <Link to="/library">
//                 <div>
//                   <img className="object-cover w-[140px] md:w-full" src="images/shahadahimg5.png" alt="Salah" />
//                   <p className='text-white text-center text-lg md:text-xl font-semi-bold md:mt-4'>Salah</p>
//                   <p className='text-orange-300 text-center text-lg md:text-xl font-semi-bold'>(Prayer)</p>
//                 </div>
//               </Link>
//             </div>

//             <div className='flex gap-4'>
//               <Link to="/library">
//                 <div>
//                   <img className="object-cover w-[140px] md:w-full" src="images/shahadahimg1.png" alt="Sawn" />
//                   <p className='text-white text-center text-lg md:text-xl font-semi-bold'>Sawn</p>
//                   <p className='text-orange-300 text-center text-lg md:text-xl font-semi-bold'>(Fasting)</p>
//                 </div>
//               </Link>

//               <Link to="/library">
//                 <div>
//                   <img className="object-cover w-[160px] md:w-full" src="images/shahadahimg2.png" alt="Hajj" />
//                   <p className='text-white text-center text-lg md:text-xl font-semi-bold'>Hajj</p>
//                   <p className='text-orange-300 text-center text-lg md:text-xl font-semi-bold'>(Pilgrim)</p>
//                 </div>
//               </Link>
//             </div>   

//             <Link to="/library">
//               <div className='flex flex-col justify-center items-center'>
//                 <img className="object-cover w-[160px] md:w-full justify-center items-center" src="images/shahadahimg3.png" alt="Zakat" />
//                 <p className='text-white text-center text-lg md:text-xl font-semi-bold md:mt-4'>Zakat</p>
//                 <p className='text-orange-300 text-center text-lg md:text-xl font-semi-bold'>(Almsgiving)</p>
//               </div>
//             </Link>
//           </div>
//         </div>
//       </div>

//       {/* Our Causes */}
//       <div className='mt-16 md:mt-24 lg:mt-32 px-4 sm:px-6 md:px-8 lg:px-12'>
//         <div className='max-w-7xl mx-auto'>
//           <div className='flex flex-col items-center'>
//             <p className='font-semibold text-lg sm:text-xl md:text-2xl text-gray-800'>Need Help For</p>
//             <p className='font-semibold text-2xl sm:text-3xl md:text-4xl mt-2'>Our Causes</p>
//             <img className="object-cover my-4 w-[180px] md:w-[200px]" src="images/hrlineimg.png" alt="hrlineimg" />
//             <p className='text-center text-sm sm:text-base md:text-lg max-w-3xl'>
//               Join hands with us to accomplish projects of serving humanity and making this world a better place to live
//             </p>
//           </div>

//           <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8 lg:gap-12 xl:gap-20 mt-8 md:mt-12'>
//             <div className='bg-[#faf8f8] rounded-lg pb-8 shadow-lg hover:shadow-2xl transition-shadow'>
//               <img className="object-cover w-full h-[260px] rounded-t-lg" src="images/masjidcon.jpg" alt="Masjid Construction" />
//               <p className='text-center text-lg md:text-xl font-semibold mt-4 px-4'>Masjid Construction</p>
//               <div className='flex justify-center mt-4'>
//                 <Link to="/donate">
//                   <button className='text-white bg-gray-800 hover:bg-gray-900 px-6 md:px-8 py-2 md:py-3 w-44 rounded-lg font-serif transition-all hover:scale-105'>
//                     Donate Now
//                   </button>
//                 </Link>
//               </div>
//             </div>
           
//             <div className='bg-[#faf8f8] rounded-lg pb-8 shadow-lg hover:shadow-2xl transition-shadow'>
//               <img className="object-cover w-full h-[260px] rounded-t-lg" src="images/services3.png" alt="Madrasa Maintenance" />
//               <p className='text-center text-lg md:text-xl font-semibold mt-4 px-4'>Madrasa Maintenance</p>
//               <div className='flex justify-center mt-4'>
//                 <Link to="/donate">
//                   <button className='text-white bg-gray-800 hover:bg-gray-900 px-6 md:px-8 py-2 md:py-3 w-44 rounded-lg font-serif transition-all hover:scale-105'>
//                     Donate Now
//                   </button>
//                 </Link>
//               </div>
//             </div>

//             <div className='bg-[#faf8f8] rounded-lg pb-8 shadow-lg hover:shadow-2xl transition-shadow'>
//               <img className="object-cover w-full h-[260px] rounded-t-lg" src="images/cause2.png" alt="Imaam & Mauzin Funds" />
//               <p className='text-center text-lg md:text-xl font-semibold mt-4 px-4'>Imaam & Mauzin Funds</p>
//               <div className='flex justify-center mt-4'>
//                 <Link to="/donate">
//                   <button className='text-white bg-gray-800 hover:bg-gray-900 px-6 md:px-8 py-2 md:py-3 w-44 rounded-lg font-serif transition-all hover:scale-105'>
//                     Donate Now
//                   </button>
//                 </Link>
//               </div>
//             </div>

//             <div className='bg-[#faf8f8] rounded-lg pb-8 shadow-lg hover:shadow-2xl transition-shadow'>
//               <img className="object-cover w-full h-[260px] rounded-t-lg" src="images/askulama.jpg" alt="Maktab & Syllabus" />
//               <p className='text-center text-lg md:text-xl font-semibold mt-4 px-4'>Maktab & Syllabus</p>
//               <div className='flex justify-center mt-4'>
//                 <Link to="/donate">
//                   <button className='text-white bg-gray-800 hover:bg-gray-900 px-6 md:px-8 py-2 md:py-3 w-44 rounded-lg font-serif transition-all hover:scale-105'>
//                     Donate Now
//                   </button>
//                 </Link>
//               </div>
//             </div>

//             <div className='bg-[#faf8f8] rounded-lg pb-8 shadow-lg hover:shadow-2xl transition-shadow'>
//               <img className="object-cover w-full h-[260px] rounded-t-lg" src="images/charity.png" alt="Social Work" />
//               <p className='text-center text-lg md:text-xl font-semibold mt-4 px-4'>Social Work</p>
//               <div className='flex justify-center mt-4'>
//                 <Link to="/donate">
//                   <button className='text-white bg-gray-800 hover:bg-gray-900 px-6 md:px-8 py-2 md:py-3 w-44 rounded-lg font-serif transition-all hover:scale-105'>
//                     Donate Now
//                   </button>
//                 </Link>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>

//       <LandingUpcomingEvents/>

//       {/* CSS for char animation */}
//       <style jsx>{`
//         .char {
//           display: inline-block;
//           color: rgb(156, 163, 175); /* gray-400 initial state */
//           opacity: 1;
//           transition: color 0.3s ease;
//         }
//       `}</style>
//     </div>
//   )
// }

// export default LandingPage






// old working main design 12/12/25


// import React, { useEffect, useRef, useState } from 'react'
// import Header from './Header'
// import VideoCarousel from './VideoCarousel'
// import LandingUpcomingEvents from './LandingUpcomingEvents'
// import { Link } from 'react-router-dom'
// import gsap from 'gsap';
// import { ScrollTrigger } from 'gsap/ScrollTrigger';

// gsap.registerPlugin(ScrollTrigger);

// function LandingPage() {
//   const [verified, setVerified] = useState(false);
//     const heroTextRef = useRef(null);
//   const imageRef = useRef(null);

//   useEffect(() => {
//     // Image Animation - Scale and Fade In
//     if (imageRef.current) {
//       gsap.fromTo(
//         imageRef.current,
//         {
//           scale: 0.8,
//           opacity: 0,
//           rotateY: -15,
//         },
//         {
//           scale: 1,
//           opacity: 1,
//           rotateY: 0,
//           duration: 1.2,
//           ease: 'power3.out',
//           scrollTrigger: {
//             trigger: imageRef.current,
//             start: 'top 80%',
//             end: 'top 50%',
//             toggleActions: 'play none none reverse',
//           },
//         }
//       );
//     }

//     // GSAP Highlight/Blur Reveal Animation - Character by Character
//     if (heroTextRef.current) {
//       const words = heroTextRef.current.querySelectorAll('.word');
      
//       // Split each word into individual characters
//       words.forEach((word) => {
//         const text = word.textContent;
//         word.innerHTML = text
//           .split('')
//           .map((char) => `<span class="char">${char === ' ' ? '&nbsp;' : char}</span>`)
//           .join('');
//       });

//       // Get all characters after splitting
//       const chars = heroTextRef.current.querySelectorAll('.char');
//       const totalChars = chars.length;

//       // Set initial color to light gray
//       chars.forEach((char) => {
//         char.style.color = 'rgb(209, 213, 219)'; // gray-300
//         char.style.opacity = 1;
//       });

//       // Create ScrollTrigger for character-by-character reveal
//       ScrollTrigger.create({
//         trigger: heroTextRef.current,
//         start: 'top 80%',
//         end: 'bottom 20%',
//         scrub: 2,
//         onUpdate: (self) => {
//           chars.forEach((char, index) => {
//             // REVERSED: Calculate progress - now animates when scrolling down
//             const charProgress = Math.max(0, Math.min(1, ((1 - self.progress) * totalChars * 1.2) - index));
            
//             // Apply color change from black (0, 0, 0) to light gray (209, 213, 219)
//             const r = Math.round(charProgress * 209);
//             const g = Math.round(charProgress * 213);
//             const b = Math.round(charProgress * 219);
            
//             char.style.color = `rgb(${r}, ${g}, ${b})`;
//             char.style.opacity = 1;
//           });
//         },
//       });
//     }

//     // Cleanup ScrollTrigger instances on unmount
//     return () => {
//       ScrollTrigger.getAll().forEach(trigger => trigger.kill());
//     };
//   }, []);

//   return (
//     <div className=' bg-[#F5F5F5 pb-[500px] md:pb-[160px] lg:pb-[120px]'>
//       <div className='  '>
//       <Header/>
//       </div>

// {/* <div className='absolute z-30 top-5 md:top-3 left-3 md:left-12'>
//   <img  className= ' w-20 md:w-32' src='images/favicon.ico'></img>
// </div> */}

// <VideoCarousel/>
// {/*landing img and content */}





// {/*landing img and content end*/}

// {/* our history */}

//  <div className='flex flex-col lg:flex-row justify-center gap-10 lg:gap-16 xl:gap-20 mt-32 items-center px-4 md:px-8 lg:px-12 max-w-7xl mx-auto'>
//       {/* Image Section */}
//       <div className='flex justify-center lg:w-1/2'>
//         <div className='relative group'>
//           <img 
//             ref={imageRef}
//             className="object-cover w-full max-w-lg rounded-2xl shadow-2xl transform transition-transform duration-500 group-hover:scale-105" 
//             src="images/cabbaborder.png" 
//             alt="Islamic Centre" 
//           />
//           {/* Decorative gradient overlay on hover */}
//           <div className='absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500' />
//         </div>
//       </div>  
      
//       {/* Text Section */}
//       <div className='flex flex-col mt-10 lg:mt-0 lg:w-1/2'>
//         {/* Header */}
//         <div className='flex flex-col items-center lg:items-start mb-8'>
//           <p className='font-semibold text-xl md:text-2xl text-gray-600 uppercase tracking-wider'>Our History</p>
//           <h2 className='font-bold text-3xl md:text-4xl lg:text-5xl text-gray-900 mt-3 leading-tight'>
//             About Jamiate <br className='hidden lg:block' />Ahlul Hadith
//           </h2>
//           <img className="object-cover my-6 w-[180px] md:w-[200px]" src="images/hrlineimg.png" alt="hrlineimg" />
//         </div>
        
//         {/* Animated Text Content */}
//         <div ref={heroTextRef} className='space-y-6'>
//           <p className='word text-justify text-base md:text-lg lg:text-xl leading-relaxed font-normal tracking-wide'>
//             Subai Jamiat Ahle Hadees Andhra Pradesh is a supervising organization overseeing all District, City, and local jamiats within Andhra Pradesh, working under Markazi Jamiat AhleHadees Hind.
//           </p>
          
//           <p className='word text-justify text-base md:text-lg lg:text-xl leading-relaxed font-normal tracking-wide'>
//             The Ahle Hadeeths, known as Salafis, are Sunni Muslims who adhere to the pristine teachings of Islam as enshrined in the Holy Quran and Sunnah. They believe in pure Monotheism (Tauheed Khalis) and reject practices not enjoined by Islamic Shariah.
//           </p>
          
//           <p className='word text-justify text-base md:text-lg lg:text-xl leading-relaxed font-normal tracking-wide'>
//             We propagate Islamic principles, values, and culture according to the Quran and Sunnah, promoting mutual benevolence, unity, peace, and brotherhood while opposing polytheism and innovations in religious matters.
//           </p>
//         </div>
        
//         {/* Call to Action Button */}
//         <div className='flex justify-center lg:justify-start mt-10'>
//           <Link to="/aboutus">
//             <button className='relative text-white bg-gradient-to-r from-gray-800 to-gray-900 hover:from-gray-900 hover:to-black px-10 py-4 text-base md:text-lg rounded-xl font-semibold transition-all duration-300 hover:scale-105 hover:shadow-2xl overflow-hidden group'>
//               <span className='relative z-10'>Learn More</span>
//               <div className='absolute inset-0 bg-gradient-to-r from-gray-700 to-gray-800 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left' />
//             </button>
//           </Link>
//         </div>
//       </div>
//     </div>
// {/* <div className='flex flex-col lg:flex-row justify-center  md:gap-20 mt-32 items-center'>

//   <div className='flex justify-center '>
//     <img className="object-cover px-2 md:px-0 rounded-2xl" src="images/cabbaborder.png" alt="Islamic Centre" />
//   </div>  

//   <div className='flex flex-col mt-10 md:mt-0'>

//     <div className='  flex flex-col items-center'>
//     <p className='font-semibold text-xl md:text-2xl text-gray-800'>Our History</p>
//         <p className='font-semibold text-2xl md:text-4xl'>About Jamiate Ahlul Hadith</p>
//         <img className="object-cover my-4 w-[200px]" src="images/hrlineimg.png" alt="hrlineimg" />
//     </div>

//      <p className='text-justify text-sm md:text-lg md:max-w-3xl p-2'>
//      About Subai Jamiat Ahle Hadees Andhra Pradesh

// Subai Jamiat Ahle Hadees Andhra Pradesh, India

// It is a Supervising organization over all District (Zilayi) jamiats, City (shehri) or local (maqami) jamiats within the state of Andhra Pradesh. This Subai Jamiat cooperates and works under Markazi Jamiat AhleHadees Hind.

// Aims and Objectives

// The Ahle Hadeeth or the Salafis, are a constituent group of Sunni Muslims.

// They adhere to the pristine teachings of Islam as enshrined in the Holy Quran and Sunnah (the precepts, practices and approvals of the Holy Prophet (S.A.W.) and reject what has not been enjoined by the Islamic Shariah.

// The Ahle Hadeeths or the Salafis believe in pristine Monotheism (Tauheed Khalis), turn to Allah seeking His Mercy and don't invoke Saint's blessings.

// They do not stick to any of the Sectarian Jurisprudence (fiqh). although they are given due respect. Since they adhere to the pristine teachings of the Holy Quran and Sunnah, they have their distinct identity among Muslims.<br/><br/>

// The Salafis interpret Quranic Verses with an anthropomorphic reference literally; They elucidate the message of Islam to all mankind and clarify the truth of pure Islamic monotheism (Towheed) according to the Quran and Sunnah;

// They propagate and publicise the Islamic principles, values, culture and ideas to all mankind according to the accepted path of righteous Salaf;

// They oppose polytheism, innovations in religious matters, (bid'at) blind imitations and reject many practices prevalent among the mystics (Sufiyas) of this day as un-Islamic accretions.

// They teach the principles of mutual benevolence, unity, peace, brotherhood, love of the country and respect of human values rejecting the causes of conflict and disunity;

// They believe in Ijtihad and not Taqleed.

// However, the four Imams are given due respect;

// They do not believe forced conversion, as the Holy Qura'n has declared: "There is no coercion (in the matter of) religion;" They condemn terrorists and terrorism as Islam is the religion of peace.
//   </p>
//     <div className='flex justify-center'>
//       <Link to="/aboutus">
//     <button className='text-white bg-gray-800 px-4 py-2 w-40 rounded-lg font-serif mt-6'>About Us</button></Link>

//     </div>
//   </div>
 
// </div> */}




// {/* namaz timings starts here */}
// <div className='flex flex-col justify-center items-center mt-10 md:mt-32'>
//     <div className='flex flex-col items-center'>
//         <p className='font-semibold text-2xl md:text-4xl'>Namaz Timings</p>
//         <img className="object-cover my-4 w-[200px]" src="images/hrlineimg.png" alt="hrlineimg" />
//     </div>

//     {/* Timing boxes */}
// {/* small screen timings */}
// <div className='block xl:hidden '>
//   <table className='w-full border-collapse text-left '>
//     <thead>
//       <tr className=' bg-gray-800 text-white'>
//         <th className='px-4 py-2  md:px-10 md:py-4 border md:text-2xl border-gray-300'>Prayer</th>
//         <th className='px-4 py-2 md:px-10 md:py-4 border md:text-2xl border-gray-300'>Adhaan</th>
//         <th className='px-4 py-2 md:px-10 md:py-4 border md:text-2xl border-gray-300'>Iqamah</th>
//       </tr>
//     </thead>
//     <tbody>
//       <tr className='odd:bg-white even:bg-gray-100'>
//         <td className='px-4 py-2 md:px-10 md:py-4 border md:text-2xl border-gray-300'>Fajr</td>
//         <td className='px-4 py-2 md:px-10 md:py-4 border md:text-2xl border-gray-300'>4:45AM</td>
//         <td className='px-4 py-2 md:px-10 md:py-4 border md:text-2xl border-gray-300'>5:00AM</td>
//       </tr>
//       <tr className='odd:bg-white even:bg-gray-100'>
//         <td className='px-4 py-2 md:px-10 md:py-4 border md:text-2xl border-gray-300'>Dhuhar</td>
//         <td className='px-4 py-2 md:px-10 md:py-4 border md:text-2xl border-gray-300'>1:00PM</td>
//         <td className='px-4 py-2 md:px-10 md:py-4 border md:text-2xl border-gray-300'>1:30PM</td>
//       </tr>
//       <tr className='odd:bg-white even:bg-gray-100'>
//         <td className='px-4 py-2 md:px-10 md:py-4  md:text-2xl border border-gray-300'>Asr</td>
//         <td className='px-4 py-2 md:px-10 md:py-4  md:text-2xl border border-gray-300'>4:00PM</td>
//         <td className='px-4 py-2 md:px-10 md:py-4  md:text-2xl border border-gray-300'>4:15PM</td>
//       </tr>
//       <tr className='odd:bg-white even:bg-gray-100'>
//         <td className='px-4 py-2 md:px-10 md:py-4 md:text-2xl border border-gray-300'>Magrib</td>
//         <td className='px-4 py-2 md:px-10 md:py-4 md:text-2xl border border-gray-300'>6:00PM</td>
//         <td className='px-4 py-2  md:px-10 md:py-4 md:text-2xl border border-gray-300'>6:06PM</td>
//       </tr>
//       <tr className='odd:bg-white even:bg-gray-100'>
//         <td className='px-4 py-2 md:px-10 md:py-4 md:text-2xl border border-gray-300'>Isha</td>
//         <td className='px-4 py-2 md:px-10 md:py-4 md:text-2xl border border-gray-300'>8:00PM</td>
//         <td className='px-4 py-2 md:px-10 md:py-4 md:text-2xl border border-gray-300'>8:30PM</td>
//       </tr>
//       <tr className='odd:bg-white   even:bg-gray-100'>
//         <td className='px-4 py-2 md:px-10 md:py-4 md:text-2xl border border-gray-300'>Jummah</td>
//         <td className='px-4 py-2 md:px-10 md:py-4 md:text-2xl border border-gray-300'>12:30PM</td>
//         <td className='px-4 py-2 md:px-10 md:py-4 md:text-2xl border border-gray-300'>1:30PM</td>
//       </tr>
//     </tbody>
//   </table>
// </div>



//     <div className=' hidden xl:flex flex-col md:flex-row gap-8 mt-8'>


//         <div className='bg-[#F1F1F1] px-4 py-8 w-40 rounded-xl transition duration-300 ease-in-out transform hover:bg-orange-200 hover:shadow-lg'>
//             <p className='text-center font-bold'>FAJR</p>
//             <p>Adhaan :<span className='text-gray-800'> 4:30AM</span></p>
//             <p>Iqamah :<span className='text-gray-800'> 5:00AM</span></p>
//         </div>

//         <div className='bg-[#F1F1F1] px-4 py-8 w-40 rounded-xl transition duration-300 ease-in-out transform hover:bg-orange-200 hover:shadow-lg'>
//             <p className='text-center font-bold'>ZUHR</p>
//             <p>Adhaan :<span className='text-gray-800'> 1:00PM</span></p>
//             <p>Iqamah :<span className='text-gray-800'> 1:30PM</span></p>
//         </div>

//         <div className='bg-[#F1F1F1] px-4 py-8 w-40 rounded-xl transition duration-300 ease-in-out transform hover:bg-orange-200 hover:shadow-lg'>
//             <p className='text-center font-bold'>ASR</p>
//             <p>Adhaan :<span className='text-gray-800'> 4:00PM</span></p>
//             <p>Iqamah :<span className='text-gray-800'> 4:15PM</span></p>
//         </div>

//         <div className='bg-[#F1F1F1] px-4 py-8 w-40 rounded-xl transition duration-300 ease-in-out transform hover:bg-orange-200 hover:shadow-lg'>
//             <p className='text-center font-bold'>MAGRIB</p>
//             <p>Adhaan :<span className='text-gray-800'> 6:00PM</span></p>
//             <p>Iqamah :<span className='text-gray-800'> 6:06PM</span></p>
//         </div>

//         <div className='bg-[#F1F1F1] px-4 py-8 w-40 rounded-xl transition duration-300 ease-in-out transform hover:bg-orange-200 hover:shadow-lg'>
//             <p className='text-center font-bold'>ISHA</p>
//             <p>Adhaan :<span className='text-gray-800'> 8:00PM</span></p>
//             <p>Iqamah :<span className='text-gray-800'> 8:30PM</span></p>
//         </div>

//         <div className='bg-[#479b83] px-4 py-8 w-40 rounded-xl transition duration-300 ease-in-out transform hover:bg-orange-200 hover:shadow-lg'>
//             <p className='text-center font-bold'>JUMMAH</p>
//             <p>Adhaan :<span className='text-white'> 12:45PM</span></p>
//             <p>Iqamah :<span className='text-white'> 1:30PM</span></p>
//         </div>

        
//     </div>
// </div>

// {/* namaz timings starts ends here */}

// {/* help our cause  starts here*/}
// <div className='flex flex-col md:flex-row justify-center  items-center xl:gap-20 mt-20'>
//   <div className='flex justify-center'>
//     <img className="object-cover lg:h-[600px]" src="images/causeimg.png" alt="Islamic Centre" />
//   </div>  

//   <div className='flex flex-col items-center mt-10 md:mt-0'>
//     <p className='font-semibold text-xl md:text-2xl text-gray-800'>Help Our Main Cause</p>
//     <p className='font-semibold text-2xl md:text-4xl'>Make Your Donation</p>
//     <img className="object-cover my-4 w-[200px]" src="images/hrlineimg.png" alt="hrlineimg" />

//     <h1 className='text-2xl md:text-3xl lg:text-4xl font-serif mt-2 text-center'>Education For Children</h1>

   

//     <div className=' hidden lg:block text-justify md:max-w-xl text-sm md:text-xl px-2 md:px-4 md:mt-4'>
//         <p>Contributed to local food drives, helping feed families in need.
//       Donated blood at a blood drive, potentially saving lives
//     Sponsored a child’s education through a nonprofit organization.
//       Supported wildlife sanctuaries and conservation efforts.</p>
//     </div>

//     <div className=' lg:hidden text-justify md:max-w-xl text-sm md:text-xl px-2 md:px-4'>
//         <p>Contributed to local food drives, helping feed families in need.
//         Donated blood at a blood drive, potentially saving lives
//         Sponsored a child’s education through a nonprofit organization.
//         Supported wildlife sanctuaries and conservation efforts.</p>
//     </div>

//     <div className='flex gap-2 lg:gap-8 mb-5 '>
//         <button className='border border-black py-1 px-2 md:px-4 rounded-lg font-serif mt-4'>100 ₹</button>
//         <button className='border border-black py-1 px-2 md:px-4 rounded-lg font-serif mt-4'>250 ₹</button>
//         <button className='border border-black py-1 px-2 md:px-4 rounded-lg font-serif mt-4'>500 ₹</button>
//         <button className='border border-black py-1 px-2 md:px-4 rounded-lg font-serif mt-4'>1000 ₹</button>
//     </div>
// <div>
// <div className='flex  items-center '>
// {/* <img className="object-cover   md:w-[500px]" src="images/sliderimg.png" alt="sliderimg" /> */}
// </div>

//  {/* <div className='flex justify-around md:gap-10 mt-2'>
// <h4 className='font-bold'>Raised:<span className='text-orange-400'> 33,000₹</span></h4>
// <h4 className='font-bold'>Goal:<span className='text-orange-400'> 50,000₹</span></h4>
//     </div> */}
// </div>
//  <div className='flex justify-center mt-4'>
//   <Link to="/donate">
// <button className='text-white bg-gray-800 md:px-4 py-2 w-44 rounded-lg font-serif'>Donate Now</button></Link>
//     </div>
// </div>
// </div>
// {/* help our cause  ends here*/}

// {/* our services starts here */}
// {/* <div className='flex flex-col  justify-center items-center mt-10 md:mt-32 lg:px-4'>
//     <div className='flex flex-col items-center'>
//     <p className='font-semibold text-xl md:text-2xl text-gray-800'>What We Offer</p>
//         <p className='font-semibold text-2xl md:text-4xl'>Our Services</p>
//         <img className="object-cover my-4 w-[200px]" src="images/hrlineimg.png" alt="hrlineimg" />
//     </div>

//     <div className='flex flex-col md:flex-row gap-14 lg:gap-6 mt-8'>
// <div className='flex flex-col lg:flex-row  gap-10  lg:gap-6 '>
// <div> 
// <img className="object-cover " src="images/servi.png" alt="hrlineimg" />
// <p className='rounded-b-xl text-white bg-gray-800 text-center text-xl  font-semi-bold'>Community Services</p>
// </div>
       
// <div>
// <img className="object-cover " src="images/chailcare1.png" alt="hrlineimg" />
// <p className='rounded-b-xl text-white bg-gray-800 text-center text-xl  font-semi-bold'>Special Child Care</p>
// </div>

// </div>

// <div className='flex flex-col lg:flex-row gap-10 lg:gap-6'>
// <div className=' '>
// <img className="object-cover " src="images/services3.png" alt="islamicclases" />
// <p className='rounded-b-xl text-white bg-gray-800 text-center text-xl  font-semi-bold'>Islamic Classes</p>
// </div>

// <div>
// <img className="object-cover " src="images/service4.png" alt="hrlineimg" />
// <p className='rounded-b-xl text-white bg-gray-800 text-center text-xl  font-semi-bold'>Masajid Constructions</p>
// </div>
// </div>
//     </div>
// </div> */}
// {/* our services ends here */}

// {/* pillers of islam starts here */}
// <div className="relative w-full lg:mt-32 mt-10">
//   <img className="object-cover object-fit w-full h-[900px] md:h-[450px] " src="images/pillersimg.png" alt="Background" />

//   <div className='absolute inset-0 mt-32 lg:mt-10 xl:mt-20  flex flex-col items-center'>
//     {/* <p className=' text-2xl text-orange-300'>About Essentials</p> */}
//         <p className=' text-2xl md:text-4xl font-serif text-white'>Pillers of Islam</p>
//         <img className="object-cover my-4 w-[200px]" src="images/hrlineimg.png" alt="hrlineimg" />

// <div className='flex flex-col gap-4 md:flex-row '>

// <div className='flex gap-4'>
// <Link to="/library">
// <div className=' '>
//         <img className="object-cover w-[150px] md:w-full" src="images/shahadahimg4.png" alt="hrlineimg" />
// <p className=' text-white text-center text-xl  font-semi-bold'>Shahadah</p>
// <p className=' text-orange-300 text-center text-xl  font-semi-bold'>(Faith)</p>
// </div></Link>

// <Link to="/library">
// <div>
//         <img className="object-cover w-[140px] md:w-full" src="images/shahadahimg5.png" alt="hrlineimg" />
// <p className=' text-white text-center text-xl  font-semi-bold md:mt-4'>Salah</p>
// <p className=' text-orange-300 text-center text-xl  font-semi-bold'>(Prayer)</p>
// </div></Link>

// </div>

//    <div className='flex gap-4'>
    
// <Link to="/library">
// <div>
//         <img className="object-cover w-[140px] md:w-full" src="images/shahadahimg1.png" alt="hrlineimg" />
// <p className=' text-white text-center text-xl  font-semi-bold'>Sawn</p>
// <p className=' text-orange-300 text-center text-xl  font-semi-bold'>(Fasting)</p>
// </div></Link>

// <Link to="/library">
// <div>
//         <img className="object-cover w-[160px] md:w-full" src="images/shahadahimg2.png" alt="hrlineimg" />
// <p className=' text-white text-center text-xl  font-semi-bold'>Hajj</p>
// <p className=' text-orange-300 text-center text-xl  font-semi-bold'>(Pilgrim)</p>
// </div></Link>

//     </div>   

//     <Link to="/library">
//     <div className='flex flex-col justify-center items-center '>
//         <img className="  object-cover w-[160px] md:w-full justify-center items-center " src="images/shahadahimg3.png" alt="hrlineimg" />
// <p className=' text-white text-center text-xl  font-semi-bold md:mt-4'>Zakat</p>
// <p className=' text-orange-300 text-center text-xl  font-semi-bold'>(Almsgiving)</p>
// </div></Link>

// </div>

//     </div>
// </div>
// {/* pillers of islam ends here */}

// {/* our causes starts here */}
// <div className='flex flex-col justify-center items-center mt-10 lg:mt-32'>
//     <div className='flex flex-col items-center mt-10 md:mt-0'>
//     <p className='font-semibold text-xl md:text-2xl text-gray-800'>Need Help For</p>
//         <p className='font-semibold text-2xl md:text-4xl'>Our Causes</p>
//         <img className="object-cover my-4 w-[200px]" src="images/hrlineimg.png" alt="hrlineimg" />
//         <p className=' text-center'>Join hands with us to accomplish all these projects of serving humanity and 
//         making these world a better place to live </p>

//     </div>

//     <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-12 xl:gap-20 mt-8 md:px-4'>

// <div className=' bg-[#faf8f8] rounded-lg pb-8 '>
// <img className="object-cover w-[370px] h-[260px] rounded-lg" src="images/masjidcon.jpg" alt="hrlineimg" />
// <p className='rounded-b-xl  text-center text-xl md:text-sm lg:text-xl font-semi-bold md:mt-4 font-semibold'>Masjid Construction</p>
//      <div className='flex justify-center mt-4'>
//       <Link to="/donate"><button className='text-white bg-gray-800  md:px-4 py-2 lg:py-3 w-44 rounded-lg font-serif'>Donate Now</button></Link>
//     </div>
// </div>
       
// <div className=' bg-[#faf8f8] rounded-lg pb-8 '>
// <img className="object-cover w-[370px] h-[260px] rounded-lg" src="images/services3.png" alt="hrlineimg" />
// <p className='rounded-b-xl  text-center md:text-sm lg:text-xl  font-semi-bold md:mt-4 font-semibold'> For Madrasa Maintenance  </p>
// <div className='flex justify-center mt-4'>
//       <Link to="/donate"><button className='text-white bg-gray-800  md:px-4 py-2 lg:py-3 w-44 rounded-lg font-serif'>Donate Now</button></Link>

//     </div>
// </div>

// <div className=' bg-[#faf8f8] rounded-lg pb-8 '>
// <img className="object-cover " src="images/cause2.png" alt="zakat" />
// <p className='rounded-b-xl  text-center md:text-sm lg:text-xl  font-semi-bold md:mt-4 font-semibold'>Imaam & Mauzin Funds</p>
// <div className='flex justify-center mt-4'>
//       <Link to="/donate"><button className='text-white bg-gray-800  md:px-4 py-2 lg:py-3 w-44 rounded-lg font-serif'>Donate Now</button></Link>
//     </div>
// </div>

// <div className=' bg-[#faf8f8] rounded-lg pb-8 '>
// <img className="object-cover w-[370px] h-[260px] rounded-lg" src="images/askulama.jpg" alt="zakat" />
// <p className='rounded-b-xl  text-center md:text-sm lg:text-xl  font-semi-bold md:mt-4 font-semibold'>Maktab & syllabaus</p>
// <div className='flex justify-center mt-4'>
//       <Link to="/donate"><button className='text-white bg-gray-800  md:px-4 py-2 lg:py-3 w-44 rounded-lg font-serif'>Donate Now</button></Link>
//     </div>
// </div>

// <div className=' bg-[#faf8f8] rounded-lg pb-8 '>
// <img className="object-cover w-[370px] h-[260px] rounded-lg" src="images/charity.png" alt="zakat" />
// <p className='rounded-b-xl  text-center md:text-sm lg:text-xl  font-semi-bold md:mt-4 font-semibold'>Social Work</p>
// <div className='flex justify-center mt-4'>
//       <Link to="/donate"><button className='text-white bg-gray-800  md:px-4 py-2 lg:py-3 w-44 rounded-lg font-serif'>Donate Now</button></Link>
//     </div>
// </div>
//     </div>
// </div>

// <LandingUpcomingEvents/>


// {/* our causes ends here */}


// {/* blogs & events starts here */}
// {/* <div className='lg:mt-20  '>
// <div className='flex flex-col items-center mt-10 lg:mt-0'>
//     <p className='font-semibold text-xl md:text-2xl text-gray-800'>Event & Blog</p>
//         <p className='font-semibold text-2xl md:text-4xl'>Our Blogs & Events</p>
//     </div>

// <div className='flex flex-col xl:flex-row xl:gap-6  2xl:gap-20 justify-center just items-center'>

// <div className='flex flex-col   xl:w-[600px] 2xl:w-[700px] '>
// <div className='flex flex-col md:flex-row items-center md:gap-10 bg-[#faf8f8] shadow-2xl rounded-lg  mt-10 ' >
// <div>
// <img className="object-cover  md:w-[300px]" src="images/blogimg.png" alt="sliderimg" />
// </div>

// <div className='' >
//     <p className='font-bold'>Is Islam Old Philosophy?</p>
//     <div className='flex gap-2 mt-4 md:mt-2 '>
// <img className="object-cover " src="images/calender.png" alt="hrlineimg" />
// <h4 className=''>147 Days Left</h4>
// <img className="object-cover " src="images/icon.png" alt="hrlineimg" />
// <h4 className=''>Inam Ullah</h4>
//     </div>
//     <p>Islam is definetly accomodates olor sit<br/>
//     amet, consecte... </p>
//     <p className=' text-orange-400 mt-4 md:mt-0'>View Details </p>
// </div>
// </div>

// <div className='flex flex-col md:flex-row items-center md:gap-10 bg-[#faf8f8] shadow-2xl rounded-lg mt-10'>
// <div>
// <img className="object-cover  md:w-[300px]" src="images/blogimg1.png" alt="sliderimg" />
// </div>

// <div className='' >
//     <p className='font-bold'>Is Islam Old Philosophy?</p>
//     <div className='flex gap-2 mt-4 md:mt-2'>
// <img className="object-cover " src="images/calender.png" alt="hrlineimg" />
// <h4 className=''>147 Days Left</h4>
// <img className="object-cover " src="images/icon.png" alt="hrlineimg" />
// <h4 className=''>Inam Ullah</h4>
//     </div>
//     <p>Islam is definetly accomodates olor sit<br/>
//     amet, consecte... </p>
//     <p className=' text-orange-400 mt-4 md:mt-0'>View Details </p>
// </div>
// </div>
// </div>

// <div className='flex flex-col gap-10 mt-20 md:mt-10 lg:mt-20 xl:mt-0 '>

// <div className=' flex flex-col md:flex-row gap-10  bg-[#eae6e6] shadow-2xl xl:px-20 py-4 rounded-xl px-2 mx-2'>
//     <div className='text-center'>
//     <p className='text-gray-800 text-5xl font-bold'>28</p>
//     <p className='text-gray-800 text-2xl'>Nov</p>
//     </div>

// <div className='flex  flex-col '>
//     <p className=' text-2xl md:text-3xl  font-mono'>World Scholars Meetup  </p>
//     <div className='flex gap-2 mt-2  items-center'>
// <img className="object-cover w-4 h-4" src="images/location.png" alt="hrlineimg" />
// <h4 className='text-sm  md:text-xl'>Park Lane, London</h4>
// <img className="object-cover w-4 h-4 " src="images/clock.png" alt="hrlineimg" />
// <h4 className='text-sm md:text-xl'>9.30AM to 1.15PM</h4>
//     </div>
// <p className='text-xl'>Event Details</p>

// </div>
// </div>

// <div className=' flex gap-10 flex-col md:flex-row  bg-[#eae6e6] shadow-2xl xl:px-20 py-4 rounded-xl px-2 mx-2'>
//     <div className='text-center'>
//     <p className='text-gray-800 text-5xl font-bold'>29</p>
//     <p className='text-gray-800 text-2xl'>Nov</p>
//     </div>

// <div className='flex  flex-col '>
//     <p className=' text-2xl md:text-3xl  font-mono'>World Scholars Meetup  </p>
//     <div className='flex gap-2 mt-2  items-center'>
// <img className="object-cover w-4 h-4" src="images/location.png" alt="hrlineimg" />
// <h4 className='text-sm  md:text-xl'>Park Lane, London</h4>
// <img className="object-cover w-4 h-4 " src="images/clock.png" alt="hrlineimg" />
// <h4 className='text-sm md:text-xl'>9.30AM to 1.15PM</h4>
//     </div>
// <p className='text-xl'>Event Details</p>

// </div>
// </div>

// <div className=' flex gap-10 flex-col md:flex-row  bg-[#eae6e6] shadow-2xl xl:px-20 py-4 rounded-xl px-2 mx-2'>
//     <div className='text-center'>
//     <p className='text-gray-800 text-5xl font-bold'>30</p>
//     <p className='text-gray-800 text-2xl'>Nov</p>
//     </div>

// <div className='flex  flex-col '>
//     <p className=' text-2xl md:text-3xl font-mono'>World Scholars Meetup  </p>
//     <div className='flex gap-2 mt-2  items-center'>
// <img className="object-cover w-4 h-4" src="images/location.png" alt="hrlineimg" />
// <h4 className='text-sm  md:text-xl'>Park Lane, London</h4>
// <img className="object-cover w-4 h-4 " src="images/clock.png" alt="hrlineimg" />
// <h4 className='text-sm md:text-xl'>9.30AM to 1.15PM</h4>
//     </div>
// <p className='text-xl'>Event Details</p>

// </div>
// </div>

// </div>
// </div>

// </div> */}
// {/* ask our Ulama starts here */}
// {/* <ContactUs/> */}

// {/* <div className='flex flex-col items-center mt-10 md:mt-20 '>
//         <p className='font-semibold text-2xl md:text-4xl text-gray-800'>Ask our Ulema</p>
//         <img className="object-cover my-4 w-[200px]" src="images/hrlineimg.png" alt="hrlineimg" />
//     </div>
// <div  className='flex flex-col lg:flex-row justify-center md:gap-32 lg:gap-16 items-center md:pb-32'>

// <div className='flex justify-center'>
//     <img className="object-cover md:h-[600px] rounded-3xl p-2" src="images/q&a.jpg" alt="Islamic Centre" />
//   </div>

// <div className="flex flex-col gap-4 md:gap-4">
//       <p className="text-2xl md:text-3xl font-bold ">Fill Your Details & Question</p>
//       <p>Name :</p>
//       <input type="text" placeholder="" className="border border-gray-300 p-2 rounded w-[300px] md:w-[500px]" />
//       <p>Number :</p>

//       <input type="text" placeholder="" className="border border-gray-300 p-2 rounded w-[300px] md:w-[500px]" />
//       <p>Email :</p>

//       <input type="text" placeholder="" className="border border-gray-300 p-2 rounded w-[300px] md:w-[500px]" />
//       <p>Your Question :</p>

//       <input type="text" placeholder="" className="border border-gray-300 p-2 h-32 rounded w-[300px] md:w-[500px]" />

//      <div className='flex justify-center mt-4'>
// <button className='text-white bg-gray-800 md:px-4 py-2 w-56 rounded-lg font-serif'>Submit</button>
//     </div>
//     </div>
// </div> */}


// {/* committee starts here */}
// {/* <div className='xl:mt-20 md:pb-96 pb-[1200px] mt-20 lg:mt-0'>
// <div className='flex flex-col items-center'>
//     <p className='font-semibold text-xl md:text-2xl text-gray-800'>Meet Our </p>
//         <p className='font-semibold text-2xl md:text-4xl'>committee Members</p>
//         <img className="object-cover my-4 w-[200px]" src="images/hrlineimg.png" alt="hrlineimg" />
//     </div>

//     <Marquee speed={200}>

//     <div className='flex   gap-10 justify-center'>

//     <div className='relative mt-10 px-4 md:px-0 md:mt-0'>
//   <img className="object-cover" src="images/expert1.png" alt="expert1" />

//   Card Half on Image
//   <div className='absolute bottom-0 left-1/2 transform -translate-x-1/2 bg-[#f7f5f5] rounded-xl w-[90%] p-4 flex flex-col items-center'>
//     Social Media Icons
//     <div className='flex gap-4 items-center mb-2'>
//       <img className="object-cover" src="images/twitter.png" alt="twitter" />
//       <img className="object-cover" src="images/fb.png" alt="facebook" />
//       <img className="object-cover" src="images/linkedin.png" alt="linkedin" />
//       <img className="object-cover" src="images/google.png" alt="google" />
//     </div>

//     Name and Title
//     <div className='text-center'>
//       <p className="underline decoration-orange-500 font-bold text-black">Bilal Hatim</p>
//       <p className="text-black">Founder & COO</p>
//     </div>
//   </div>
// </div>

// <div className='relative mt-10 px-4 md:px-0 md:mt-0'>
//   <img className="object-cover" src="images/expert2.png" alt="expert1" />

//   <div className='absolute  bottom-0 left-1/2 transform -translate-x-1/2 bg-[#f7f5f5] rounded-xl w-[90%] p-4 flex flex-col items-center'>
//     Social Media Icons
//     <div className='flex gap-4 items-center mb-2'>
//       <img className="object-cover" src="images/twitter.png" alt="twitter" />
//       <img className="object-cover" src="images/fb.png" alt="facebook" />
//       <img className="object-cover" src="images/linkedin.png" alt="linkedin" />
//       <img className="object-cover" src="images/google.png" alt="google" />
//     </div>

//     Name and Title
//     <div className='text-center'>
//       <p className="underline decoration-orange-500 font-bold text-black">Ali Hammam</p>
//       <p className="text-black">Islamic Scholar</p>
//     </div>
//   </div>
// </div>

// <div className='relative mt-10 px-4 md:px-0 md:mt-0'>
//   <img className="object-cover" src="images/expert3.png" alt="expert1" />

//   Card Half on Image
//   <div className='absolute bottom-0 left-1/2 transform -translate-x-1/2 bg-[#f7f5f5] rounded-xl w-[90%] p-4 flex flex-col  items-center'>
//     Social Media Icons
//     <div className='flex gap-4 items-center mb-2'>
//       <img className="object-cover" src="images/twitter.png" alt="twitter" />
//       <img className="object-cover" src="images/fb.png" alt="facebook" />
//       <img className="object-cover" src="images/linkedin.png" alt="linkedin" />
//       <img className="object-cover" src="images/google.png" alt="google" />
//     </div>

//     Name and Title
//     <div className='text-center'>
//       <p className="underline decoration-orange-500 font-bold text-black">Naseer Sheik</p>
//       <p className="text-black">Volunteer</p>
//     </div>
//   </div>
// </div>
//     </div>

//     </Marquee>

// </div> */}


//     </div>
//   )
// }

// export default LandingPage